import React, { FC, useRef, useEffect, useState } from 'react';
import { EffectCreative } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react/swiper-react.js';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { STATIC_MEDIA_DOMAIN } from '@src/constants';
import { Skeleton } from '@src/containers/Source/TalentCard/Skeleton';
import { TalentCard } from '@src/containers/Source/TalentCard';
import { GroupedTalentDictionaryKey } from '@src/containers/Source/constants/groupedTalentDictionary/enums/groupedTalentDictionaryKey.enum';
import { EmptyState } from '../EmptyState';
import { TaCarouselProps } from './props';
import './styles.scss';

export const TalentCarousel: FC<TaCarouselProps> = ({
  usersData,
  isInitialFetchLoading,
  isFetchMoreLoading,
  isSwiperSet,
  setSwiper,
  onSlideChange,
  openingId,
  openingTitle,
  onConfirmTalent,
  onDiscardTalent,
  hasMoreResults,
  invitedUserIds,
  swiperActiveIndex,
  openingState,
  goBack,
  goNext,
  isKeyboardShortcutsDisable,
  groupedTalentsKey,
}) => {
  const [isKeyboardDisable, setIsKeyboardDisable] = useState(false);

  const prevOpeningId = useRef<string | undefined>(openingId);
  const isSavedTalentsActive = groupedTalentsKey === GroupedTalentDictionaryKey.SAVED_TALENTS;

  useEffect(() => {
    prevOpeningId.current = openingId;
  }, [openingId]);

  useEffect(() => {
    const onKeyDown = debounce(event => {
      const code = event.code;

      switch (code) {
        case 'ArrowLeft':
        case 'Left':
          goBack?.();
          break;
        case 'ArrowRight':
        case 'Right':
          goNext?.();
          break;
      }
    }, 200);

    if (isSwiperSet && !isKeyboardShortcutsDisable && !isKeyboardDisable) {
      document.addEventListener('keydown', onKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isSwiperSet, isKeyboardShortcutsDisable, isKeyboardDisable]);

  if (isInitialFetchLoading) {
    return <Skeleton wrapperClassName="ta-swiper-wrapper__initial-loading" />;
  }

  const getEmptyStateContent = () => {
    if (isSavedTalentsActive) {
      return {
        text: (
          <>
            Start browsing talent by searching of exploring <br /> categories and save talent by clicking "Save"
          </>
        ),
        title: "You don't have any saved talents yet!",
      };
    }

    return {
      text: (
        <>
          Check back soon to see more suitable <br />
          candidates. We’ll also notify you via email.{' '}
        </>
      ),
      title: 'You’re all caught up!',
    };
  };

  const renderEmptyState = () => {
    return (
      <div className="ta-swiper-wrapper__empty-state">
        <EmptyState
          imageUrl={`${STATIC_MEDIA_DOMAIN}/images/ta-empty-state.png`}
          text={getEmptyStateContent().text}
          title={getEmptyStateContent().title}
        />
      </div>
    );
  };

  if (!isInitialFetchLoading && usersData && !usersData.length) {
    return renderEmptyState();
  }

  return (
    <div className="ta-swiper-wrapper">
      <Swiper
        allowTouchMove={false}
        centeredSlides={false}
        creativeEffect={{
          next: {
            scale: 0.92,
            translate: ['100%', 0, 0],
          },
          prev: {
            shadow: false,
            translate: [0, 0, -400],
          },
        }}
        effect={'creative'}
        loop={false}
        modules={[EffectCreative]}
        onSlideChange={swiper => {
          swiper.update();
          onSlideChange(swiper);
        }}
        onSwiper={swiper => setSwiper(swiper)}
        slidesPerView={1.12}
      >
        {usersData.map((candidate, index) => {
          return (
            <SwiperSlide key={candidate.user.id}>
              {({ isActive }) => {
                return index + 3 > swiperActiveIndex && index - 3 < swiperActiveIndex ? (
                  <TalentCard
                    goBack={goBack}
                    goNext={goNext}
                    isFooterVisible={true}
                    isHideActionVisible={!isSavedTalentsActive}
                    isInvited={!!invitedUserIds.includes(candidate.user.id)}
                    isKeyboardDisable={isKeyboardDisable}
                    isKeyboardShortcutsDisable={!!isKeyboardShortcutsDisable}
                    isVisible={isActive}
                    onConfirmTalent={params => onConfirmTalent(candidate.user.id, params)}
                    onDiscardTalent={onDiscardTalent}
                    openingId={openingId}
                    openingState={openingState}
                    openingTitle={openingTitle}
                    setIsKeyboardDisable={setIsKeyboardDisable}
                    userId={candidate.user.id}
                    userPartialData={candidate}
                    wrapperClassName={classnames(!isActive && 'ta-swiper-wrapper__card--events-disabled')}
                  />
                ) : null;
              }}
            </SwiperSlide>
          );
        })}

        {!isFetchMoreLoading && !hasMoreResults && !isSavedTalentsActive ? (
          <SwiperSlide key="ta-carousel-card-no-result">{renderEmptyState()}</SwiperSlide>
        ) : null}

        {isFetchMoreLoading ? (
          <>
            <SwiperSlide key="ta-carousel-card-skeleton">
              <Skeleton />
            </SwiperSlide>

            <SwiperSlide key="ta-carousel-card-skeleton-2">
              <Skeleton />
            </SwiperSlide>
          </>
        ) : null}
      </Swiper>
    </div>
  );
};
